import { useCallback, useEffect, useState } from 'react';
import checkFeatureFlagByName from '../api/feature-flags/checkFeatureFlagByName';

/**
 * Flag de la sección de traspasos con las tarjetas de Proa (chirigota#586). Se resuelve por
 * `user_ids` en la tabla `feature_flags` de comparsa, así que se puede abrir a unas pocas
 * personas en PROD antes de darla por buena: en DEV no hay datos reales de traspasos.
 */
export const SECCION_TRASPASOS_FEATURE_FLAG = 'seccion-traspasos';

export interface UseSeccionTraspasosEnabledResult {
	enabled: boolean;
	loading: boolean;
	/**
	 * Ya se sabe la respuesta (encendida, apagada o fallida) a la última pregunta; mientras se
	 * vuelve a preguntar, `false`. `loading` no sirve para eso: arranca en `false`, antes de preguntar. Lo usa el Dashboard para no decidir el aterrizaje a ciegas.
	 */
	comprobada: boolean;
}

/**
 * ¿Ve esta persona la sección de traspasos? Abre la tarjeta de acción de Inicio (identidad,
 * destino o firma) y las secciones de traspasos de Tu beneficio, Movimientos y Configurar.
 *
 * **Ante cualquier duda, apagado**: sin token, sin usuario, con la petición fallando o con la
 * flag sin crear (el backend devuelve `isActive: false` si no existe), la pantalla queda como
 * estaba, sin la sección.
 *
 * ⚠️ En `feature_flags`, `user_ids` VACÍO no es «nadie», es «sin restricción de usuario»: con
 * `is_active = true` y la lista vacía se abre a todo el parque.
 */
const useSeccionTraspasosEnabled = (
	token: string | null | undefined,
	companyId: string | null | undefined,
	userId: string | null | undefined
): UseSeccionTraspasosEnabledResult => {
	const [enabled, setEnabled] = useState(false);
	const [loading, setLoading] = useState(false);
	const [comprobada, setComprobada] = useState(false);

	const check = useCallback(async () => {
		if (!token || !userId) {
			setEnabled(false);
			setComprobada(true);
			return;
		}

		setLoading(true);
		// Al volver a preguntar (otro usuario, otro token), lo de antes ya no vale.
		setComprobada(false);

		try {
			const res = await checkFeatureFlagByName(
				token,
				companyId ?? '',
				userId,
				SECCION_TRASPASOS_FEATURE_FLAG
			);
			setEnabled(!!(res?.success && res.data?.isActive === true));
		} catch {
			// Un fallo de red no puede abrir la sección a todo el mundo.
			setEnabled(false);
		} finally {
			setLoading(false);
			setComprobada(true);
		}
	}, [token, companyId, userId]);

	useEffect(() => {
		check();
	}, [check]);

	return { enabled, loading, comprobada };
};

export default useSeccionTraspasosEnabled;
